import { Award, Calendar, Star, Trophy, Info, CheckCircle } from "lucide-react";

const gradeLevels = [
  {
    level: "Level 1",
    color: "bg-[#3FA9F5]",
    description: "Foundation skills and basic fencing techniques",
  },
  {
    level: "Level 2",
    color: "bg-[#D4A857]",
    description: "Intermediate skills and tactical awareness",
  },
  {
    level: "Level 3",
    color: "bg-[#0A1A2F]",
    description: "Advanced techniques and competitive readiness",
  },
];

export default function GradingSection() {
  return (
    <section
      id="grading"
      className="py-20 bg-white"
      data-testid="grading-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#D4A857]/10 text-[#D4A857] px-4 py-2 rounded text-sm font-bold uppercase tracking-wide mb-4 font-['Montserrat']">
            <Award size={16} />
            Official Recognition
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A1A2F] mb-4 font-['Montserrat']">
            British Fencing <span className="text-[#D4A857]">Grading Awards</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-['Lato']">
            Celebrate your child's progress with nationally recognised achievement awards from British Fencing
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Main Info */}
          <div className="space-y-6">
            {/* Key Info Card */}
            <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-[#D4A857]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D4A857] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="text-[#0A1A2F]" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1A2F] text-lg mb-2 font-['Montserrat']">When Does Grading Happen?</h3>
                  <p className="text-gray-600 leading-relaxed font-['Lato']">
                    British Fencing Grading Awards take place <span className="font-bold text-[#D4A857]">every Friday</span> during school holidays. 
                    Fencers can choose to work towards their Level 1, Level 2, or Level 3 grades throughout the year.
                  </p>
                </div>
              </div>
            </div>

            {/* Grade Levels */}
            <div className="bg-white rounded-lg p-6 shadow-lg border border-[#F4F4F4]">
              <h3 className="font-bold text-[#0A1A2F] text-lg mb-4 flex items-center gap-2 font-['Montserrat']">
                <Trophy className="text-[#D4A857]" size={20} />
                Achievement Levels
              </h3>
              <div className="space-y-3">
                {gradeLevels.map((grade, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-[#F4F4F4] rounded-lg">
                    <div className={`${grade.color} w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm font-['Montserrat']`}>
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-bold text-[#0A1A2F] font-['Montserrat']">{grade.level}</p>
                      <p className="text-gray-500 text-sm font-['Lato']">{grade.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-[#0A1A2F] rounded-lg p-6 text-white">
              <h3 className="font-bold text-[#D4A857] mb-4 flex items-center gap-2 font-['Montserrat'] uppercase text-sm">
                <Info size={20} />
                Important Information
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#D4A857] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-white/90 font-['Lato']">Grading is <span className="font-bold text-white">optional</span> but a great way for children to set goals and celebrate their progress</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#D4A857] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-white/90 font-['Lato']">All grading happens on <span className="font-bold text-[#D4A857]">Fridays only</span> — no other days are available</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#D4A857] flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-white/90 font-['Lato']"><span className="font-bold text-white">New to fencing?</span> Your child must complete their grading on their final day, so please ensure their last day of camp is a Friday if they wish to grade</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Side - Visual */}
          <div className="space-y-6">
            {/* Award Visual */}
            <div className="bg-[#0A1A2F] rounded-lg p-8 text-white text-center relative overflow-hidden">
              <div className="relative z-10">
                <div className="w-20 h-20 bg-[#D4A857] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-10 h-10 text-[#0A1A2F]" />
                </div>
                <h3 className="text-2xl font-bold mb-2 font-['Montserrat']">Official British Fencing Awards</h3>
                <p className="text-white/80 mb-6 font-['Lato']">
                  Nationally recognised grades that celebrate your child's fencing journey
                </p>
                
                <div className="flex justify-center gap-4">
                  <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                    <Star className="w-6 h-6 mx-auto mb-1 text-[#D4A857]" />
                    <p className="text-xs font-['Lato']">Certificates</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                    <Award className="w-6 h-6 mx-auto mb-1 text-[#D4A857]" />
                    <p className="text-xs font-['Lato']">Pin Badges</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                    <Trophy className="w-6 h-6 mx-auto mb-1 text-[#D4A857]" />
                    <p className="text-xs font-['Lato']">Recognition</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Masky Tip - Small */}
            <div className="bg-[#F4F4F4] rounded-lg p-6 flex items-start gap-4 border border-[#F4F4F4]">
              <img
                src="https://customer-assets.emergentagent.com/job_1abea0ec-cded-4678-a29f-de28c813ea65/artifacts/2pfnnew9_bigger%20masky.png"
                alt="Masky"
                className="w-28 h-28 flex-shrink-0"
              />
              <div>
                <p className="font-bold text-[#D4A857] mb-2 font-['Montserrat'] uppercase text-sm">Masky's Tip!</p>
                <p className="text-gray-600 text-sm leading-relaxed font-['Lato']">
                  "If your child wants to earn their British Fencing grade, make sure to book their 
                  <span className="font-bold text-[#0A1A2F]"> last day on a Friday</span>! 
                  That way they can show off everything they've learned."
                </p>
              </div>
            </div>

            {/* Learn More Link */}
            <a
              href="https://www.britishfencing.com/achievement-awards/"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-lg p-4 hover:bg-[#F4F4F4] transition-colors group border border-[#F4F4F4]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-[#0A1A2F] font-['Montserrat']">Learn More About British Fencing Awards</p>
                  <p className="text-gray-500 text-sm font-['Lato']">Visit the official British Fencing website</p>
                </div>
                <span className="text-[#D4A857] group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
